import { motion, useMotionValue, animate, useInView } from "framer-motion";
import { useEffect, useRef } from "react";
import { ShieldCheck, Clock, Award, MapPin } from "lucide-react";
import { REVEAL } from "@/lib/site";

const Counter = ({ to, suffix = "" }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  const mv = useMotionValue(0);
  const out = useRef(null);
  useEffect(() => {
    if (!inView) return;
    const controls = animate(mv, to, {
      duration: 1.6,
      ease: "easeOut",
      onUpdate: (v) => {
        if (out.current) out.current.textContent = Math.floor(v).toLocaleString() + suffix;
      },
    });
    return controls.stop;
  }, [inView, to, suffix, mv]);
  return <span ref={(el) => { ref.current = el; out.current = el; }}>0{suffix}</span>;
};

const perks = [
  { icon: Clock, title: "Fast dispatch", text: "Around 40-minute arrival across metro Sydney, day or night." },
  { icon: ShieldCheck, title: "Fully insured", text: "EPA & Dangerous Goods compliant. Your car is in safe hands." },
  { icon: Award, title: "Work guaranteed", text: "No fix, no fee. If we can't start it, you don't pay a cent." },
  { icon: MapPin, title: "We come to you", text: "Roadside, driveway or servo — our mobile unit finds you." },
];

const stats = [
  { to: 40, suffix: "min", label: "Average response" },
  { to: 24, suffix: "/7", label: "Always on call" },
];

export const WhyUs = () => (
  <section id="why" data-testid="why-section" className="border-b border-black/10 bg-[#F5F3EF] py-24 md:py-32">
    <div className="mx-auto max-w-7xl px-5">
      <div>
        <div>
          <motion.div variants={REVEAL} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.4 }}>
            <span className="kicker text-[#1F7A6B]">Why Fuel Hero</span>
            <h2 className="mt-3 font-heading text-3xl md:text-5xl leading-tight text-[#171717]">
              You're in good hands
            </h2>
          </motion.div>

          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {perks.map((p, i) => (
              <motion.div
                key={p.title}
                variants={REVEAL}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, amount: 0.3 }}
                transition={{ delay: i * 0.06 }}
                className="rounded-2xl border border-black/10 bg-white p-5 shadow-soft-sm"
                data-testid={`perk-${i + 1}`}
              >
                <span className="grid h-10 w-10 place-items-center rounded-lg bg-[#E7DFCF] text-[#171717]">
                  <p.icon strokeWidth={2} className="h-5 w-5" />
                </span>
                <h3 className="mt-3 font-heading text-lg text-[#171717]">{p.title}</h3>
                <p className="mt-1 text-sm leading-relaxed text-[#171717]/65">{p.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-14 grid gap-4 sm:grid-cols-2">
        {stats.map((s, i) => (
          <motion.div
            key={s.label}
            variants={REVEAL}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.3 }}
            transition={{ delay: i * 0.1 }}
            className="rounded-2xl bg-[#22303C] p-8 text-center"
            data-testid={`stat-${i + 1}`}
          >
            <div className="font-heading text-4xl md:text-5xl text-white">
              <Counter to={s.to} suffix={s.suffix} />
            </div>
            <div className="mt-2 text-sm font-medium text-white/60">{s.label}</div>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);
